import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Combine, Copy, DollarSign, ExternalLink, Minus, Pencil, Plus, Trash2, X } from 'lucide-react';

import { Combobox } from '@/components/Combobox';
import {
  useAddBottles, useDeleteWine, useDiscardBottles, useMergeWine, useWine, useWines,
} from './useWines';
import { WineImageGallery } from './WineImageGallery';
import { WineTastingsPanel } from '@/features/tastings/WineTastingsPanel';
import { WinePurchasesPanel } from '@/features/purchases/WinePurchasesPanel';
import { WineBoxesPanel } from '@/features/boxes/WineBoxesPanel';
import { fmtUsd } from '@/lib/format';

export function WineDetailPage() {
  const params = useParams();
  const id = Number(params.id);
  const navigate = useNavigate();
  const { data: wine, isLoading } = useWine(id);
  const [showAddBottles, setShowAddBottles] = useState(false);
  const [showDiscardBottles, setShowDiscardBottles] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const deleteWine = useDeleteWine();

  if (isLoading) return <div className="p-4 md:p-8 text-notion-500">Cargando…</div>;
  if (!wine) return <div className="p-4 md:p-8 text-notion-500">Vino no encontrado.</div>;

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <Link to="/wines" className="text-sm text-notion-500 inline-flex items-center gap-1 mb-3">
        <ArrowLeft size={14} /> Volver
      </Link>

      <header className="mb-4 md:mb-6">
        <div className="mb-3">
          <h1 className="text-xl md:text-2xl font-medium leading-tight">{wine.name}</h1>
          <p className="text-sm text-notion-500">
            {wine.winery?.name} · {wine.vintage ?? '—'} · {wine.type_label} ({wine.classification_label})
          </p>
        </div>

        {/* Acciones — botón primario "Precio" prominente en mobile */}
        <div className="flex flex-wrap gap-2">
          <Link to={`/wines/${wine.id}/price`} className="btn-primary">
            <DollarSign size={14} /> Registrar precio
          </Link>
          <Link to={`/wines/new?duplicate_from=${wine.id}`} className="btn-secondary">
            <Copy size={14} /> Duplicar
          </Link>
          <Link to={`/wines/${wine.id}/edit`} className="btn-secondary">
            <Pencil size={14} /> Editar
          </Link>
          <button
            type="button"
            onClick={() => setShowMerge(true)}
            className="btn-secondary"
            title="Unir con otro vino (elimina este vino y mueve todo su contenido al otro)"
          >
            <Combine size={14} /> Unir con...
          </button>
          <button
            type="button"
            onClick={async () => {
              const stock = wine.current_stock ?? 0;
              const warning = stock > 0
                ? `Este vino tiene ${stock} botella${stock === 1 ? '' : 's'} en stock. Si se creó por error y hay otro vino igual, mejor usá "Unir con..." para no perder la historia.\n\n¿Igual querés eliminarlo?`
                : '¿Eliminar este vino? Queda archivado (soft-delete). Se puede restaurar por consola si hiciera falta.';
              if (!confirm(warning)) return;
              try {
                await deleteWine.mutateAsync(wine.id);
                navigate('/wines');
              } catch (err: any) {
                const msg = err?.response?.data?.message ?? 'No se pudo eliminar el vino.';
                alert(msg);
              }
            }}
            disabled={deleteWine.isPending}
            className="btn-secondary text-rose-700 border-rose-200 hover:bg-rose-50"
            title="Eliminar vino (soft-delete)"
          >
            <Trash2 size={14} /> Eliminar
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
        <WineImageGallery wineId={wine.id} images={wine.images ?? []} />
        <WinePurchasesPanel wineId={wine.id} />
        <WineBoxesPanel wineId={wine.id} />
        <WineTastingsPanel wineId={wine.id} />
        {/* Card de stock primero en mobile (información clave de un vistazo) */}
        <div className="card p-4 md:p-5 space-y-3 order-first lg:order-last">
          <h2 className="font-medium">Stock y valores</h2>
          <p className="text-3xl font-medium text-notion-800">
            {wine.current_stock ?? 0}
            <span className="text-base font-normal text-notion-500"> botellas</span>
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setShowAddBottles(true)}
              className="btn-secondary justify-center"
            >
              <Plus size={14} /> Agregar
            </button>
            <button
              type="button"
              onClick={() => setShowDiscardBottles(true)}
              disabled={(wine.current_stock ?? 0) === 0}
              className="btn-secondary justify-center disabled:opacity-40 disabled:cursor-not-allowed"
              title="Descontar botellas por error de registro"
            >
              <Minus size={14} /> Descontar
            </button>
          </div>
          <div className="text-sm space-y-1 pt-2 border-t border-notion-100">
            <p>Costo prom: <strong>{fmtUsd(wine.avg_cost_usd ?? null)}</strong></p>
            <p>Valor estimado: <strong>{fmtUsd(wine.estimated_value_usd ?? null)}</strong></p>
            {wine.estimated_value_usd && wine.avg_cost_usd && (
              <p className="text-xs text-notion-500">
                Diferencia: {fmtUsd(wine.estimated_value_usd - wine.avg_cost_usd)} / botella
              </p>
            )}
          </div>
        </div>

        <div className="card p-4 md:p-5 lg:col-span-2 space-y-3">
          <h2 className="font-medium">Ficha</h2>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div><dt className="text-notion-500">Procedencia</dt><dd>{wine.place?.display_path ?? wine.place?.name ?? '—'}</dd></div>
            <div><dt className="text-notion-500">INV</dt><dd>{wine.inv_number ?? '—'}</dd></div>
            <div><dt className="text-notion-500">Alcohol</dt><dd>{wine.alcohol ? `${wine.alcohol}%` : '—'}</dd></div>
            <div><dt className="text-notion-500">PH</dt><dd>{wine.ph ?? '—'}</dd></div>
            <div><dt className="text-notion-500">Capacidad</dt><dd>{wine.format}</dd></div>
            <div><dt className="text-notion-500">Forma botella</dt><dd>{wine.bottle_shape_label ?? '—'}</dd></div>

            <div className="col-span-2">
              <dt className="text-notion-500">Productor/es</dt>
              <dd className="flex flex-wrap gap-2 mt-1">
                {wine.producers?.length ? wine.producers.map((p) => (
                  <span key={p.id} className="text-xs px-2 py-1 rounded-full bg-notion-100 text-notion-700">
                    {p.name}{p.role ? ` · ${p.role}` : ''}
                  </span>
                )) : <span>—</span>}
              </dd>
            </div>
            <div className="col-span-2">
              <dt className="text-notion-500">Enólogo/s</dt>
              <dd className="flex flex-wrap gap-2 mt-1">
                {wine.enologists?.length ? wine.enologists.map((e) => (
                  <span key={e.id} className="text-xs px-2 py-1 rounded-full bg-notion-100 text-notion-700">
                    {e.name}{e.role ? ` · ${e.role}` : ''}
                  </span>
                )) : <span>—</span>}
              </dd>
            </div>
            <div className="col-span-2">
              <dt className="text-notion-500">Cepas</dt>
              <dd className="flex flex-wrap gap-2 mt-1">
                {wine.grape_varieties?.length ? wine.grape_varieties.map((g) => (
                  <span key={g.id} className="text-xs px-2 py-1 rounded-full bg-notion-50 text-notion-800">
                    {g.name}{g.percentage ? ` ${g.percentage}%` : ''}
                  </span>
                )) : <span>—</span>}
              </dd>
            </div>
          </dl>

          {wine.notes && (
            <div className="pt-3 border-t border-notion-100">
              <p className="text-notion-500 text-sm mb-1">Notas</p>
              <p className="text-sm whitespace-pre-line">{wine.notes}</p>
            </div>
          )}

          {wine.technical_sheet_url && (
            <a href={wine.technical_sheet_url} target="_blank" rel="noreferrer" className="text-sm text-notion-800 inline-flex items-center gap-1 mt-2">
              Ver ficha técnica <ExternalLink size={14} />
            </a>
          )}
        </div>
      </div>

      {showAddBottles && (
        <AddBottlesModal wineId={wine.id} onClose={() => setShowAddBottles(false)} />
      )}
      {showDiscardBottles && (
        <DiscardBottlesModal
          wineId={wine.id}
          maxQuantity={wine.current_stock ?? 0}
          onClose={() => setShowDiscardBottles(false)}
        />
      )}
      {showMerge && (
        <MergeWineModal
          sourceWineId={wine.id}
          sourceLabel={`${wine.winery?.name ? wine.winery.name + ' · ' : ''}${wine.name}${wine.vintage ? ` ${wine.vintage}` : ''}`}
          onClose={() => setShowMerge(false)}
          onMerged={(targetId) => {
            setShowMerge(false);
            navigate(`/wines/${targetId}`);
          }}
        />
      )}
    </div>
  );
}

/**
 * Mini-modal para sumar botellas al stock del vino. Es un ajuste manual
 * (no es una compra) — útil cuando aparece una botella que no estaba
 * registrada o cuando se quiere subir el stock rápido.
 */
function AddBottlesModal({ wineId, onClose }: { wineId: number; onClose: () => void }) {
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const addBottles = useAddBottles();

  const submit = async () => {
    if (quantity < 1) return;
    await addBottles.mutateAsync({ id: wineId, quantity, notes: notes || undefined });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl p-4 max-w-sm w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <p className="font-medium text-notion-800">Agregar botellas al stock</p>
            <p className="text-xs text-notion-500">Ajuste manual (no es una compra).</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-notion-500 hover:text-notion-800"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <label className="label">Cantidad</label>
        <div className="flex items-center gap-2 mb-3">
          <button
            type="button"
            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
            className="w-10 h-10 rounded-md border border-notion-200 hover:border-notion-400 text-lg"
          >
            −
          </button>
          <input
            type="number"
            min={1}
            max={200}
            value={quantity}
            onChange={(e) => setQuantity(Math.max(1, Math.min(200, Number(e.target.value) || 1)))}
            className="input text-center text-lg font-medium flex-1"
          />
          <button
            type="button"
            onClick={() => setQuantity((q) => Math.min(200, q + 1))}
            className="w-10 h-10 rounded-md border border-notion-200 hover:border-notion-400 text-lg"
          >
            +
          </button>
        </div>

        <label className="label">Notas (opcional)</label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Ej: encontradas en el sótano"
          className="input mb-3"
        />

        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="btn-secondary flex-1">
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={addBottles.isPending || quantity < 1}
            className="btn-primary flex-1"
          >
            {addBottles.isPending ? 'Agregando…' : `Agregar ${quantity}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Modal para descontar botellas del stock por error de registro (no es
 * consumo / regalo / merma). Internamente las marca con razón "adjustment".
 */
function DiscardBottlesModal({
  wineId,
  maxQuantity,
  onClose,
}: {
  wineId: number;
  maxQuantity: number;
  onClose: () => void;
}) {
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const discardBottles = useDiscardBottles();

  const submit = async () => {
    if (quantity < 1 || quantity > maxQuantity) return;
    await discardBottles.mutateAsync({ id: wineId, quantity, notes: notes || undefined });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl p-4 max-w-sm w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <p className="font-medium text-notion-800">Descontar botellas</p>
            <p className="text-xs text-notion-500">
              Ajuste por error de registro. Stock actual: <strong>{maxQuantity}</strong>.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-notion-500 hover:text-notion-800"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <label className="label">Cantidad a descontar</label>
        <div className="flex items-center gap-2 mb-3">
          <button
            type="button"
            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
            className="w-10 h-10 rounded-md border border-notion-200 hover:border-notion-400 text-lg"
          >
            −
          </button>
          <input
            type="number"
            min={1}
            max={maxQuantity}
            value={quantity}
            onChange={(e) =>
              setQuantity(Math.max(1, Math.min(maxQuantity, Number(e.target.value) || 1)))
            }
            className="input text-center text-lg font-medium flex-1"
          />
          <button
            type="button"
            onClick={() => setQuantity((q) => Math.min(maxQuantity, q + 1))}
            className="w-10 h-10 rounded-md border border-notion-200 hover:border-notion-400 text-lg"
          >
            +
          </button>
        </div>

        <label className="label">Notas (opcional)</label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Ej: estaban duplicadas en el import"
          className="input mb-3"
        />

        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="btn-secondary flex-1">
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={discardBottles.isPending || quantity < 1 || quantity > maxQuantity}
            className="btn-primary flex-1 bg-rose-600 hover:bg-rose-700"
          >
            {discardBottles.isPending ? 'Descontando…' : `Descontar ${quantity}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Modal para fusionar el vino actual con otro. El vino actual (source) queda
 * soft-deleted y todo su stock, compras, catas, precios, botellas, cajas,
 * imágenes y aliases se transfieren al target.
 */
function MergeWineModal({
  sourceWineId,
  sourceLabel,
  onClose,
  onMerged,
}: {
  sourceWineId: number;
  sourceLabel: string;
  onClose: () => void;
  onMerged: (targetId: number) => void;
}) {
  const [targetId, setTargetId] = useState<number | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const { data: wines } = useWines({ per_page: 500 });
  const mergeWine = useMergeWine();

  const options =
    wines?.data
      ?.filter((w) => w.id !== sourceWineId)
      .map((w) => ({
        id: w.id,
        label: `${w.name}${w.vintage ? ` ${w.vintage}` : ''} · ${w.format}`,
        hint: w.winery?.name ?? undefined,
      })) ?? [];

  const targetWine = wines?.data?.find((w) => w.id === targetId);

  const submit = async () => {
    if (!targetId) return;
    try {
      await mergeWine.mutateAsync({ sourceId: sourceWineId, targetId });
      onMerged(targetId);
    } catch (err: any) {
      const msg = err?.response?.data?.message
        ?? Object.values(err?.response?.data?.errors ?? {})[0]?.toString()
        ?? 'No se pudo unir el vino.';
      alert(msg);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl p-4 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <p className="font-medium text-notion-800">Unir con otro vino</p>
            <p className="text-xs text-notion-500">
              Este vino se elimina y todo pasa al que elijas (stock, compras, precios, catas, botellas, cajas, fotos, aliases).
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-notion-500 hover:text-notion-800" aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>

        <div className="rounded-md bg-notion-50 border border-notion-200 p-2.5 mb-3 text-sm">
          <p className="text-notion-500 text-xs">Este vino (desaparece):</p>
          <p className="font-medium text-notion-800">{sourceLabel}</p>
        </div>

        <label className="label">Se une al vino:</label>
        <Combobox
          value={targetId}
          placeholder="Buscar por nombre, bodega…"
          options={options}
          onChange={(id) => {
            setTargetId(id ? Number(id) : null);
            setConfirmed(false);
          }}
        />

        {targetWine && (
          <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2.5 mt-2 text-sm">
            <p className="text-emerald-800 text-xs">Este vino sobrevive:</p>
            <p className="font-medium text-notion-800">
              {targetWine.winery?.name ? `${targetWine.winery.name} · ` : ''}
              {targetWine.name}
              {targetWine.vintage ? ` ${targetWine.vintage}` : ''}
            </p>
          </div>
        )}

        <label className="mt-3 inline-flex items-start gap-2 text-xs text-notion-700 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-0.5"
            disabled={!targetId}
          />
          <span>Entiendo que esto elimina el vino actual y no se puede deshacer desde la UI (queda soft-deleted en la base).</span>
        </label>

        <div className="flex gap-2 mt-3">
          <button type="button" onClick={onClose} className="btn-secondary flex-1">
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!targetId || !confirmed || mergeWine.isPending}
            className="btn-primary flex-1"
          >
            {mergeWine.isPending ? 'Uniendo…' : 'Unir vinos'}
          </button>
        </div>
      </div>
    </div>
  );
}
